import React, { useState, useEffect, useRef } from 'react';
import styles from './SubmitInvoiceForm.module.css';
import { expenseTypeOptions, categoryOptions, subCategoryMap } from '../../config/variables';
import { GET_EXCHANGE_RATE, GET_INVOICES } from '../../services/graphql';
import { numericInputError } from '../../utils/numericInput';
import { useLazyQuery, useQuery } from '@apollo/client';

// Empty string is what every text input wants; NaN and null are not displayable.
// Saved tab data carries amounts as parseFloat output, so NaN arrives routinely.
const displayValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && Number.isNaN(value)) return '';
  return value;
};

/**
 * Map a saved tab payload (or a loaded invoice) onto form field values.
 *
 * Shared by the mount-time seed and the edit-mode re-sync so both produce
 * identical state - previously only the effect did this, which meant a freshly
 * mounted form started blank and published those blanks before the effect ran.
 */
const toFormState = (values = {}, canSubmitDistanceInvoices = true) => ({
  expenseType: values.expenseType || '',
  category: values.category || '',
  // Mileage is not selectable without the permission, so never seed it either.
  subCategory: (!canSubmitDistanceInvoices && values.subCategory === 'Mileage')
    ? ''
    : (values.subCategory || ''),
  project: values.project || '',
  distance: canSubmitDistanceInvoices ? (values.distance || '') : '',
  entity: values.entity || '',
  allocation: values.allocation || '',
  // amountText preserves exactly what was typed; amount is the parsed float and
  // is all that edit mode supplies.
  amount: displayValue(values.amountText !== undefined ? values.amountText : values.amount),
  currency: values.currency || 'CAD',
  gst: values.gst || '',
  pst: values.pst || '',
  userNote: values.userNote || '',
  details: values.details || '',
  CADAmount: displayValue(values.CADAmount),
  distanceTrips: Array.isArray(values.distanceTrips)
    ? values.distanceTrips.map((trip, index) => ({
        sortOrder: trip.sortOrder ?? index,
        tripDate: trip.tripDate ? String(trip.tripDate).slice(0, 10) : '',
        fromLocation: trip.fromLocation || '',
        toLocation: trip.toLocation || '',
        distance: trip.distance ?? '',
        notes: trip.notes || '',
      }))
    : [],
});

const SubmitInvoiceForm = ({
  onSubmit,
  initialValues,
  prefillEnabled = false,
  canSubmitDistanceInvoices = true,
  customDistanceRate = null,
}) => {

  const currencyOptions = ['CAD', 'USD']; // todo: retrieve from backend
  const entityOptions = ['TTG', 'FEC'];
  const allocationOptions = [
    'Charitable Activities',
    'Management & Admin',
    'Fundraising',
  ];
  const projectOptions = [
    'Back Office',
    'Charitable',
    'Crypto',
    'E-Presence',
    'Middle Office',
    'O & G',
    'Overhead',
    'Software',
    'Trading',
    'Capstone',
    'Other',
  ];
  const provinceRatesByYear = {
    2022: { first: 0.61, additional: 0.55 },
    2023: { first: 0.68, additional: 0.62 },
    2024: { first: 0.70, additional: 0.64 },
    2025: { first: 0.72, additional: 0.66 },
    2026: { first: 0.73, additional: 0.67 }
  };

  // Seeded once, at mount. The submit page gives this component key={activeTab},
  // so switching tabs remounts it and this runs again with that tab's saved data.
  const [seed] = useState(() => toFormState(initialValues || {}, canSubmitDistanceInvoices));

  const [expenseType, setExpenseType] = useState(seed.expenseType);
  const [category, setCategory] = useState(seed.category);
  const [subCategory, setSubCategory] = useState(seed.subCategory);
  const [project, setProject] = useState(seed.project);
  const [distance, setDistance] = useState(seed.distance);
  const [entity, setEntity] = useState(seed.entity);
  const [allocation, setAllocation] = useState(seed.allocation);
  const [amount, setAmount] = useState(seed.amount);
  const [currency, setCurrency] = useState(seed.currency);
  const [gst, setGst] = useState(seed.gst);
  const [pst, setPst] = useState(seed.pst);
  const [userNote, setUserNote] = useState(seed.userNote);
  const [details, setDetails] = useState(seed.details);
  const [exchangeRate, setExchangeRate] = useState();
  const [CADAmount, setCADAmount] = useState(seed.CADAmount);
  const [ytdDistance, setYtdDistance] = useState(0);
  const [distanceTrips, setDistanceTrips] = useState(seed.distanceTrips);
  const numericAmount = Number(amount);
  const hasValidAmount = Number.isFinite(numericAmount) && numericAmount > 0;
  const numericExchangeRate = Number(exchangeRate);
  const hasValidExchangeRate = Number.isFinite(numericExchangeRate) && numericExchangeRate > 0;


  // Edit mode only. There the invoice arrives from a query after this component has
  // already mounted, so the seed above was empty and the fields have to be filled in
  // once the data lands. The submit page must not take this path: it remounts per tab,
  // and re-syncing from initialValues there would overwrite what the user is typing,
  // because every keystroke is published back up into initialValues.
  useEffect(() => {
    if (!prefillEnabled || !initialValues) return;

    const next = toFormState(initialValues, canSubmitDistanceInvoices);
    setExpenseType(next.expenseType);
    setCategory(next.category);
    setSubCategory(next.subCategory);
    setProject(next.project);
    setDistance(next.distance);
    setDistanceTrips(next.distanceTrips);
    setEntity(next.entity);
    setAllocation(next.allocation);
    setAmount(next.amount);
    setCurrency(next.currency);
    setGst(next.gst);
    setPst(next.pst);
    setUserNote(next.userNote);
    setDetails(next.details);
    setCADAmount(next.CADAmount);
  }, [initialValues, prefillEnabled, canSubmitDistanceInvoices]);

  const hasTripData = (trips = distanceTrips) => trips.some((trip) => (
    trip.tripDate ||
    trip.fromLocation ||
    trip.toLocation ||
    trip.notes ||
    String(trip.distance ?? '').trim() !== ''
  ));

  const isMileageSelection = (nextCategory = category, nextSubCategory = subCategory) => (
    nextCategory === 'Travel' && nextSubCategory === 'Mileage'
  );

  const clearMileageTrips = () => {
    setDistanceTrips([]);
    setDistance('');
  };

  const confirmLeavingMileage = (nextCategory, nextSubCategory) => {
    if (!isMileageSelection() || isMileageSelection(nextCategory, nextSubCategory) || !hasTripData()) {
      return true;
    }
    if (window.confirm('Changing away from Mileage will remove all trip details. Continue?')) {
      clearMileageTrips();
      return true;
    }
    return false;
  };

  const handleCategoryChange = (value) => {
    const nextSubCategory = subCategoryMap[value]?.includes(subCategory) ? subCategory : '';
    if (!confirmLeavingMileage(value, nextSubCategory)) return;
    setCategory(value);
    setSubCategory(nextSubCategory);
  };

  const handleSubCategoryChange = (value) => {
    if (!confirmLeavingMileage(category, value)) return;
    setSubCategory(value);
  };

  const handleInputChange = (setter, value) => {
    setter(value);
  };

  const getProvinceRates = (year) => {
    const availableYears = Object.keys(provinceRatesByYear).map(Number).sort((a, b) => a - b);
    const effectiveYear = provinceRatesByYear[year]
      ? year
      : availableYears[availableYears.length - 1];
    const defaultRates = provinceRatesByYear[effectiveYear];
    const customRate = Number(customDistanceRate);
    if (Number.isFinite(customRate) && customRate > 0) {
      return {
        first: customRate,
        additional: customRate,
      };
    }
    return {
      first: defaultRates.first,
      additional: defaultRates.additional,
    };
  };

  const isDistanceBased = distance !== '' && !isNaN(parseFloat(distance)) && parseFloat(distance) > 0;
  const isMileage = canSubmitDistanceInvoices && isMileageSelection();

  // Shown live under the numeric fields. Submission is blocked separately in
  // SubmitInvoicePage, so a bad value can never reach the numeric columns.
  // Sub Total is skipped while read-only, since mileage invoices compute it.
  // Must stay below isDistanceBased - these are const, so referencing it earlier
  // is a temporal dead zone error at render.
  const amountError = isDistanceBased ? null : numericInputError(amount, 'Sub Total');
  const gstError = numericInputError(gst, 'GST');
  const pstError = numericInputError(pst, 'PST');
  const subCategoryOptions = (subCategoryMap[category] || []).filter(
    (option) => canSubmitDistanceInvoices || option !== 'Mileage'
  );

  const addMileageTrip = () => {
    setDistanceTrips((prevTrips) => [
      ...prevTrips,
      {
        sortOrder: prevTrips.length,
        tripDate: '',
        fromLocation: '',
        toLocation: '',
        distance: '',
        notes: '',
      },
    ]);
  };

  const updateMileageTrip = (index, field, value) => {
    setDistanceTrips((prevTrips) => prevTrips.map((trip, tripIndex) => (
      tripIndex === index ? { ...trip, [field]: value } : trip
    )));
  };

  const removeMileageTrip = (index) => {
    setDistanceTrips((prevTrips) => prevTrips
      .filter((_, tripIndex) => tripIndex !== index)
      .map((trip, tripIndex) => ({ ...trip, sortOrder: tripIndex })));
  };

  useEffect(() => {
    if (!canSubmitDistanceInvoices && subCategory === 'Mileage') {
      setSubCategory('');
    }
  }, [canSubmitDistanceInvoices, subCategory]);

  useEffect(() => {
    if (!canSubmitDistanceInvoices && distance !== '') {
      setDistance('');
    }
  }, [canSubmitDistanceInvoices, distance]);

  useEffect(() => {
    if (isMileage && distanceTrips.length === 0) {
      setDistanceTrips([{
        sortOrder: 0,
        tripDate: '',
        fromLocation: '',
        toLocation: '',
        distance: '',
        notes: '',
      }]);
    }
  }, [isMileage, distanceTrips.length]);

  // Clear a Sub Total that distance itself computed, but only when distance actually
  // goes from having a value to empty. Without the transition check this fired on
  // mount for every ordinary invoice - where distance is legitimately empty - and
  // wiped the seeded Sub Total, which is what made it look like the field cleared
  // itself on tab switches.
  const previousDistanceRef = useRef(seed.distance);
  useEffect(() => {
    const previousDistance = previousDistanceRef.current;
    previousDistanceRef.current = distance;

    if (!isMileage && distance === '' && previousDistance !== '') {
      setAmount('');
    }
  }, [distance, isMileage]);

  useEffect(() => {
    if (!isMileage) return;
    const totalDistance = distanceTrips.reduce((total, trip) => {
      const tripDistance = Number(trip.distance);
      return Number.isFinite(tripDistance) && tripDistance > 0 ? total + tripDistance : total;
    }, 0);
    const nextDistance = totalDistance > 0 ? Number(totalDistance.toFixed(2)).toString() : '';
    if (distance !== nextDistance) {
      setDistance(nextDistance);
    }
  }, [isMileage, distanceTrips, distance]);

  const { data: invoicesData } = useQuery(GET_INVOICES);

  useEffect(() => {
    const invoiceYear = new Date().getFullYear();
    const invoices = invoicesData?.getInvoices?.body || [];
    let total = 0;
    invoices.forEach((invoice) => {
      const year = parseInt((invoice?.date || '').slice(0, 4), 10);
      if (year !== invoiceYear) return;
      const dist = parseFloat(invoice?.distance);
      if (!isNaN(dist)) total += dist;
    });
    setYtdDistance(total);
  }, [invoicesData]);

  const [getExchangeRate, { data: exchangeRateData }] = useLazyQuery(GET_EXCHANGE_RATE);

  useEffect(() => {
    const rate = Number(exchangeRateData?.getExchangeRate?.body);
    const isValidRate = Number.isFinite(rate) && rate > 0;
    setExchangeRate(isValidRate ? rate : undefined);
    setCADAmount(hasValidAmount && isValidRate ? numericAmount * rate : '');
  }, [exchangeRateData, hasValidAmount, numericAmount]);

  useEffect(() => {
    if (!isDistanceBased) return;
    const distanceValue = parseFloat(distance);
    if (isNaN(distanceValue) || distanceValue <= 0) return;
    if (currency !== 'CAD') {
      setCurrency('CAD');
    }
    const invoiceYear = new Date().getFullYear();
    const rates = getProvinceRates(invoiceYear);
    const totalBefore = ytdDistance;
    const totalAfter = totalBefore + distanceValue;
    let computedAmount = 0;
    if (totalBefore >= 5000) {
      computedAmount = distanceValue * rates.additional;
    } else if (totalAfter <= 5000) {
      computedAmount = distanceValue * rates.first;
    } else {
      const firstPortion = 5000 - totalBefore;
      const additionalPortion = totalAfter - 5000;
      computedAmount = (firstPortion * rates.first) + (additionalPortion * rates.additional);
    }
    const computedAmountStr = computedAmount.toFixed(2);
    if (amount !== computedAmountStr) {
      setAmount(computedAmountStr);
    }
  }, [distance, ytdDistance, customDistanceRate]);

  useEffect(() => {
  const floatAmount = parseFloat(amount, 10);
  const floatCADAmount = parseFloat(CADAmount, 10);
  onSubmit({
    expenseType,
    category,
    subCategory,
    project,
    distance,
    entity,
    allocation,
    amount: floatAmount,
    CADAmount: floatCADAmount,
    currency,
    // Numeric fields are passed up raw as well, so the submit gate can tell
    // "invalid" apart from "left blank" - parseFloat turns both into NaN.
    amountText: amount,
    gst,
    pst,
    userNote,
	    details,
      distanceTrips: isMileage
        ? distanceTrips.map((trip, index) => ({
            sortOrder: index,
            tripDate: trip.tripDate,
            fromLocation: trip.fromLocation,
            toLocation: trip.toLocation,
            distance: trip.distance === '' ? null : Number(trip.distance),
            notes: trip.notes,
          }))
        : [],
	    // Automatically fill with system date
    date: new Date().toISOString().slice(0, 10), // → "YYYY-MM-DD",
  });
	}, [expenseType, category, subCategory, project, distance, entity, allocation, amount, currency, gst, pst, userNote, details, CADAmount, distanceTrips, isMileage]);

  useEffect(() => {
    getExchangeRate({
      variables: {
        currency: currency,
      }
    })
  }, [currency, amount, getExchangeRate])

  return (
    <div className={styles.formCard}>
      <p className={styles.sectionSubtitle}>
        Expense information
      </p>
      <div className={styles.formRow}>
        <div className={styles.inputGroup}>
          <label className={styles.label}>Expense Type*</label>
          <select
            className={styles.inputField}
            value={expenseType}
            onChange={(e) => handleInputChange(setExpenseType, e.target.value)}
            required
          >
            <option value="">Select Expense Type</option>
            {expenseTypeOptions.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.label}>Category*</label>
          <select
	            className={styles.inputField}
	            value={category}
	            onChange={(e) => handleCategoryChange(e.target.value)}
	            required
          >
            <option value="">Select Category</option>
            {categoryOptions.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.label}>Sub-category*</label>
          <select
	            className={styles.inputField}
	            value={subCategory}
	            onChange={(e) => handleSubCategoryChange(e.target.value)}
	            required
          >
            <option value="">Select Sub-category</option>
            {subCategoryOptions.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.formRow}>
        <div className={styles.inputGroup}>
          <label className={styles.label}>Entity*</label>
          <select
            className={styles.inputField}
            value={entity}
            onChange={(e) => handleInputChange(setEntity, e.target.value)}
            required
          >
            <option value="">Entity</option>
            {entityOptions.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.label}>Sub Total ($)*</label>
          <input
            className={styles.inputField}
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={amount}
            onChange={(e) => handleInputChange(setAmount, e.target.value)}
            readOnly={isDistanceBased}
            required
            aria-invalid={Boolean(amountError)}
          />
          {amountError && <div className={styles.fieldError}>{amountError}</div>}
          {(currency !== 'CAD' && hasValidAmount) && (
            <div className={styles.conversionText}>
              {hasValidExchangeRate
                ? `Sub Total in CAD: ${(numericAmount * numericExchangeRate).toFixed(2)}`
                : 'Sub Total in CAD: calculating...'}
            </div>
          )}
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.label}>Currency*</label>
          <select
            className={styles.inputField}
            value={currency}
            onChange={(e) => handleInputChange(setCurrency, e.target.value)}
            disabled={isDistanceBased}
            required
          >
            {currencyOptions.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.formRow}>
        <div className={styles.inputGroup}>
          <label className={styles.label}>GST ($)</label>
          <input
            className={styles.inputField}
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={gst}
            onChange={(e) => handleInputChange(setGst, e.target.value)}
            aria-invalid={Boolean(gstError)}
          />
          {gstError && <div className={styles.fieldError}>{gstError}</div>}
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.label}>PST ($)</label>
          <input
            className={styles.inputField}
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={pst}
            onChange={(e) => handleInputChange(setPst, e.target.value)}
            aria-invalid={Boolean(pstError)}
          />
          {pstError && <div className={styles.fieldError}>{pstError}</div>}
        </div>
        {canSubmitDistanceInvoices && isMileage ? (
          <div className={styles.inputGroup}>
            <div className={styles.labelWithInfo}>
              <label className={styles.label}>Total Distance</label>
              <button
                type="button"
                className={styles.infoIcon}
                title="Distance help"
                data-tooltip="Total distance is calculated from mileage trips."
                aria-label="Distance help: total distance is calculated from mileage trips."
              >
                ?
              </button>
            </div>
            <div className={styles.unitFieldWrap}>
              <input
                className={`${styles.inputField} ${styles.unitInput}`}
                type="text"
                value={distance}
                readOnly
              />
              <span className={styles.unitSuffix}>Km</span>
            </div>
          </div>
        ) : (
          <div className={styles.inputGroup}>
            <label className={styles.label}>Distance</label>
            <input
              className={styles.inputField}
              type="text"
              value=""
              disabled
              readOnly
              placeholder="Not available"
            />
          </div>
        )}
      </div>

      {isMileage && (
        <div className={styles.tripSection}>
          <div className={styles.tripSectionHeader}>
            <p className={styles.sectionSubtitle}>
              Mileage trips
            </p>
            <button type="button" className={styles.addTripButton} onClick={addMileageTrip}>
              + Add Trip
            </button>
          </div>
          <div className={styles.tripTable}>
            {distanceTrips.map((trip, index) => (
              <div className={styles.tripCard} key={index}>
                <div className={styles.tripCardHeader}>
                  <div className={styles.tripNumber}>Trip {index + 1}</div>
                  <button
                    type="button"
                    className={styles.removeTripButton}
                    onClick={() => removeMileageTrip(index)}
                    aria-label={`Remove trip ${index + 1}`}
                  >
                    Remove
                  </button>
                </div>
                <div className={styles.tripFields}>
                  <div className={styles.tripField}>
                    <label className={styles.tripLabel}>Date*</label>
                    <input
                      className={styles.inputField}
                      type="date"
                      value={trip.tripDate}
                      onChange={(e) => updateMileageTrip(index, 'tripDate', e.target.value)}
                      required
                    />
                  </div>
                  <div className={styles.tripField}>
                    <label className={styles.tripLabel}>From*</label>
                    <input
                      className={styles.inputField}
                      type="text"
                      placeholder="Source"
                      value={trip.fromLocation}
                      onChange={(e) => updateMileageTrip(index, 'fromLocation', e.target.value)}
                      required
                    />
                  </div>
                  <div className={styles.tripField}>
                    <label className={styles.tripLabel}>To*</label>
                    <input
                      className={styles.inputField}
                      type="text"
                      placeholder="Destination"
                      value={trip.toLocation}
                      onChange={(e) => updateMileageTrip(index, 'toLocation', e.target.value)}
                      required
                    />
                  </div>
                  <div className={styles.tripField}>
                    <label className={styles.tripLabel}>Distance*</label>
                    <div className={styles.unitFieldWrap}>
                      <input
                        className={`${styles.inputField} ${styles.unitInput}`}
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0"
                        value={trip.distance}
                        onChange={(e) => updateMileageTrip(index, 'distance', e.target.value)}
                        required
                      />
                      <span className={styles.unitSuffix}>Km</span>
                    </div>
                  </div>
                  <div className={`${styles.tripField} ${styles.tripNotesField}`}>
                    <label className={styles.tripLabel}>Notes</label>
                    <input
                      className={styles.inputField}
                      type="text"
                      placeholder="Optional"
                      value={trip.notes}
                      onChange={(e) => updateMileageTrip(index, 'notes', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          {distanceTrips.length === 0 && (
            <button type="button" className={styles.emptyTripButton} onClick={addMileageTrip}>
              + Add first trip
            </button>
          )}
        </div>
      )}

      <div className={styles.formRow}>
        <div className={styles.inputGroup}>
          <label className={styles.label}>Project</label>
          <select
            className={styles.inputField}
            value={project}
            onChange={(e) => handleInputChange(setProject, e.target.value)}
          >
            <option value="">Select Project</option>
            {projectOptions.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.label}>Allocation</label>
          <select
            className={styles.inputField}
            value={allocation}
            onChange={(e) => handleInputChange(setAllocation, e.target.value)}
          >
            <option value="">Select Allocation</option>
            {allocationOptions.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.inputGroup}>
          <div className={styles.labelWithInfo}>
            <label className={styles.label}>Details</label>
            <button
              type="button"
              className={styles.infoIcon}
              title="Brief description of the invoice purpose"
              data-tooltip="For eg: Taxi from YYC airport to home"
              aria-label="Details help: brief description of the invoice purpose"
            >
              ?
            </button>
          </div>
          <input
            className={`${styles.inputField} ${styles.detailsField}`}
            type="text"
            value={details}
            onChange={(e) => handleInputChange(setDetails, e.target.value)}
          />
        </div>
      </div>

      <div className={styles.formRow}>
        <div className={`${styles.inputGroup} ${styles.spanFull}`}>
          <div className={styles.labelWithInfo}>
            <label className={styles.label}>User Note</label>
            <button
              type="button"
              className={styles.infoIcon}
              title="Optional note for approvers"
              data-tooltip="For eg: The taxi fare is higher than usual because of the snowstorm on that day."
              aria-label="User Note help: optional note for approvers"
            >
              ?
            </button>
          </div>
          <textarea
            className={styles.userNoteField}
            value={userNote}
            onChange={(e) => handleInputChange(setUserNote, e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default SubmitInvoiceForm;
